import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "../ui-custom/Card";
import Button from "../ui-custom/Button";
import { Input } from "../ui-custom/Input";
import { Users, Plus, Search, Settings2 } from "lucide-react";
import { AllocateFundsDialog } from "./AllocateFundsDialog";
import { UpdateSpendableLimitDialog } from "./UpdateSpendableLimitDialog";
import { LuSettings2 } from "react-icons/lu";

function getInitials(name) {
  return name.split(" ").map(n => n[0]).join("").toUpperCase().slice(0, 2);
}

function getAvatarColor(name) {
  const colors = [
    "bg-sm-accent/15 text-sm-accent",
    "bg-dark-green/15 text-dark-green",
    "bg-sm-secondary/15 text-sm-secondary",
    "bg-sm-primary/15 text-sm-primary",
    "bg-destructive/15 text-destructive",
  ];
  const index = name.charCodeAt(0) % colors.length;
  return colors[index];
}

function isOwnerEmployee(emp) {
  const roleText = [emp?.role, emp?.designation, emp?.profession, emp?.employee_profession]
    .filter(Boolean)
    .join(" ");
  return /\bowner\b/i.test(roleText);
}

export function PettyCashAllocations({
  employeeBalances,
  onAllocateFunds,
  onUpdateSpendableLimit,
  loadingTeamAllocations = false,
}) {
  const [isAllocateDialogOpen, setIsAllocateDialogOpen] = useState(false);
  const [isUpdateLimitOpen, setIsUpdateLimitOpen] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");

  const filteredEmployees = (employeeBalances || [])
    .filter((emp) => !isOwnerEmployee(emp))
    .filter((emp) => emp.name.toLowerCase().includes(searchQuery.toLowerCase()));

  return (
    <Card>
      <CardHeader className="pb-2 px-1">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="p-1.5 bg-sm-secondary/10 rounded">
              <Users className="w-4 h-4 text-sm-secondary" />
            </div>
            <CardTitle className="text-base font-semibold">Team Allocations</CardTitle>
          </div>
          <div className="flex items-center gap-3">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-text-gray" />
              <Input
                placeholder="Search team..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9 h-9 w-48"
              />
            </div>
            <Button
              onClick={() => setIsAllocateDialogOpen(true)}
              variant="primary"
            >
              <Plus className="w-4 h-4 mr-2" />
              Allocate
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent className="pt-0 space-y-1">
        {loadingTeamAllocations && (
          <div className="text-center py-8 text-text-gray text-sm">Loading team allocations...</div>
        )}
        {filteredEmployees.map((emp) => (
          <div key={emp.id} className="flex items-center justify-between py-4 border-b border-sm-border last:border-0">
            <div className="flex items-center gap-3">
              {
                emp.userpic ? (
                  <img src={emp.userpic} alt={emp.name} className="w-11 h-11 rounded-full" style={{ objectFit: "cover" }} />
                ) : (
                  <div className={`w-11 h-11 rounded-full flex items-center justify-center text-sm font-medium ${getAvatarColor(emp.name)}`}>
                    {getInitials(emp.name)}
                  </div>
                )
              }
              <div>
                <div className="flex items-center gap-2">
                  <span className="font-medium text-primary-text">{emp.name}</span>
                  <span className="text-xs px-2 py-0.5 bg-sm-secondary/15 text-sm-secondary rounded font-medium">
                    {emp.role}
                  </span>
                </div>
                <p className="text-sm text-text-gray">{emp.project}</p>
              </div>
            </div>
            <div className="flex items-center gap-8 text-right text-sm">
              <div>
                <p className="text-xs text-text-gray mb-0.5">Allocated</p>
                <p className="font-semibold text-primary-text">₹{emp.totalAllocated.toLocaleString("en-IN")}</p>
              </div>
              <div>
                <p className="text-xs text-text-gray mb-0.5">Spent</p>
                <p className="font-semibold text-sm-accent">₹{(emp.totalAllocated - emp.balance).toLocaleString("en-IN")}</p>
              </div>
              <div>
                <p className="text-xs text-text-gray mb-0.5">Balance</p>
                <p className="font-semibold text-dark-green">₹{emp.balance.toLocaleString("en-IN")}</p>
              </div>
              <div className="flex items-center gap-2">
                <div>
                  <p className="text-xs text-text-gray mb-0.5">Spendable</p>
                  <p className="font-semibold text-sm-secondary">₹{(emp.spendableAmount || 0).toLocaleString("en-IN")}</p>
                </div>
                <button
                  variant="default"
                  size="sm"
                  className="h-8 w-8 p-2 flex flex-col items-center hover:text-sm-secondary rounded-full hover:bg-sm-secondary/10"
                  onClick={() => {
                    setSelectedEmployee(emp);
                    setIsUpdateLimitOpen(true);
                  }}
                  title="Update Spendable Limit"
                >
                  <LuSettings2 className="h-4 w-4" />
                </button>
              </div>
            </div>
          </div>
        ))}

        {filteredEmployees.length === 0 && (
          <div className="text-center py-12 text-text-gray">
            No team members found
          </div>
        )}
      </CardContent>

      <AllocateFundsDialog
        open={isAllocateDialogOpen}
        onOpenChange={setIsAllocateDialogOpen}
        onSubmit={onAllocateFunds}
      />

      <UpdateSpendableLimitDialog
        open={isUpdateLimitOpen}
        onOpenChange={setIsUpdateLimitOpen}
        employee={selectedEmployee}
        onSubmit={onUpdateSpendableLimit}
      />
    </Card>
  );
}
